import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io, type Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { env } from "@/config/env";
import { useAuth } from "@/api/auth";

interface SocketContextValue {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextValue>({ socket: null });

const EVENT_INVALIDATION_MAP: Record<string, string[]> = {
  "review.created": ["page", "media-review", "page", "content-workbench"],
  "review.decision.updated": ["page", "media-review"],
  "review.completed": ["page", "media-review"],
  "listing.intake.updated": ["page", "listing-intake"],
  "listing.release.updated": ["page", "final-listing-approval", "page", "content-workbench"],
  "listing.published": [
    "page",
    "final-listing-approval",
    "dashboard",
    "command-center",
  ],
  "deal.updated": ["page", "dealz-control", "page", "livedealz-intake"],
  "deal.status.changed": ["page", "dealz-control"],
  "campaign.status.changed": ["page", "campaigns"],
  "supplier.status.changed": ["page", "suppliers-providers"],
  "user.updated": ["page", "roles-permissions"],
  "access-request.decided": ["page", "roles-permissions", "page", "inbox"],
  "content.version.created": ["page", "content-workbench", "listing", ""],
  "content.approved": ["page", "content-workbench", "listing", ""],
  "automation.job.queued": ["page", "automation"],
  "automation.job.completed": ["page", "automation"],
  "automation.job.failed": ["page", "automation"],
  "work_item.assigned": ["operations", "work-items", "page", "inbox"],
  "inbox.item.created": ["page", "inbox", "dashboard", "app-shell"],
  "platform.setting.updated": ["page", "platform-settings"],
  "platform.settings.changed": ["page", "platform-settings"],
  "asset.created": ["page", "user-asset-libraries"],
  "media_project.completed": [
    "page",
    "image-builder",
    "page",
    "video-slideshow-builder",
  ],
};

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, principal } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [activeSocket, setActiveSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!accessToken || !principal) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setActiveSocket(null);
      }
      return;
    }

    const socket = io(`${env.apiBaseUrl}/events`, {
      transports: ["websocket", "polling"],
      auth: { token: accessToken },
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;
    setActiveSocket(socket);

    socket.on("connect", () => {
      if (env.debug) console.log("[socket] connected", socket.id);
    });

    socket.on("disconnect", (reason) => {
      if (env.debug) console.log("[socket] disconnected", reason);
    });

    socket.on("connect_error", (error) => {
      if (env.debug) console.error("[socket] connect error", error.message);
    });

    Object.entries(EVENT_INVALIDATION_MAP).forEach(([event, keys]) => {
      socket.on(event, (payload: unknown) => {
        if (env.debug) console.log(`[socket] ${event}`, payload);
        for (let i = 0; i < keys.length; i += 2) {
          const queryKey = keys[i + 1] ? [keys[i], keys[i + 1]] : [keys[i]];
          queryClient.invalidateQueries({ queryKey });
        }
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setActiveSocket(null);
    };
  }, [accessToken, principal, queryClient]);

  return (
    <SocketContext.Provider value={{ socket: activeSocket }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}
